import React, { useState, useEffect, useCallback } from 'react';
import { FaBox } from 'react-icons/fa';
import * as clientService from '../../services/client.service';
import * as clientCatalogService from '../../services/clientCatalog.service';
import { productService } from '../../services/product.service';
import TearSheetModal from '../../components/TearSheet/TearSheetModal';
import ClientCatalogPageHeader from '../../components/ClientCatalog/ClientCatalogPageHeader';
import ClientCatalogClientBar from '../../components/ClientCatalog/ClientCatalogClientBar';
import ClientCatalogProductGrid from '../../components/ClientCatalog/ClientCatalogProductGrid';
import ClientCatalogImportModal from '../../components/ClientCatalog/ClientCatalogImportModal';
import { createEmptyImportFilters } from './clientCatalogUtils';
import { CLIENT_CATALOG } from '../../constant';
import './ClientCatalog.css';

const ClientCatalog = () => {
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientDropdown, setShowClientDropdown] = useState(false);

  const [clientCatalogProducts, setClientCatalogProducts] = useState([]);

  const [showImportModal, setShowImportModal] = useState(false);
  const [importableProducts, setImportableProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [importFilters, setImportFilters] = useState(() => createEmptyImportFilters());
  const [filterOptions, setFilterOptions] = useState({
    category: [],
    productName: [],
    finish: [],
    color: [],
    suitableFor: [],
    material: [],
    collection: [],
    packType: [],
  });

  const [showFiltersInModal, setShowFiltersInModal] = useState(true);

  const [tearSheetOpen, setTearSheetOpen] = useState(false);
  const [tearSheetProduct, setTearSheetProduct] = useState(null);

  useEffect(() => {
    const loadClients = async () => {
      try {
        const result = await clientService.getClientList();
        setClients(Array.isArray(result.data) ? result.data : []);
      } catch (error) {
        console.error('Error loading clients:', error);
      }
    };
    loadClients();
  }, []);

  useEffect(() => {
    if (selectedClientId) {
      const client = clients.find((c) => String(c.id) === String(selectedClientId));
      setSelectedClient(client);
      if (client) {
        loadClientCatalog(client.clientId);
      }
    }
  }, [selectedClientId, clients]);

  const loadClientCatalog = async (clientId) => {
    try {
      if (!clientId) {
        console.error('Client ID not found');
        return;
      }

      const catalog = await clientCatalogService.getClientCatalog(clientId);
      setClientCatalogProducts(catalog);
    } catch (error) {
      console.error('Error loading catalog:', error);
      alert(CLIENT_CATALOG.ALERT_LOAD_CATALOG_FAILED);
    }
  };

  const loadFilterOptions = useCallback(async () => {
    try {
      const options = await productService.getFilterOptions();
      setFilterOptions(options);
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  }, []);

  useEffect(() => {
    if (!showImportModal) return;

    const loadProducts = async () => {
      try {
        const apiFilters = {
          search: searchTerm,
          ...importFilters,
        };
        const allProducts = await productService.getCatalogProducts({
          ...apiFilters,
          limit: CLIENT_CATALOG.IMPORT_LIMIT,
        });
        const paginatedProducts = allProducts.data || [];
        const importedUIDs = new Set(clientCatalogProducts.map((p) => p.uid));
        const filtered = paginatedProducts.filter((product) => !importedUIDs.has(product.uid));
        setImportableProducts(filtered);
      } catch (error) {
        console.error('Error loading importable products:', error);
      }
    };

    loadProducts();
  }, [showImportModal, searchTerm, importFilters, clientCatalogProducts]);

  const handleOpenImport = () => {
    if (!selectedClientId) {
      alert(CLIENT_CATALOG.ALERT_SELECT_CLIENT_FIRST);
      return;
    }
    loadFilterOptions();
    setShowImportModal(true);
  };

  const toggleProductSelection = (productId) => {
    setSelectedProducts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const reloadCatalogAfterMutation = async () => {
    const cid =
      selectedClient?.clientId ??
      selectedClient?.client_id ??
      clients.find((c) => String(c._id || c.id) === String(selectedClientId))?.clientId ??
      clients.find((c) => String(c.id) === String(selectedClientId))?.client_id;
    if (cid) await loadClientCatalog(cid);
  };

  const handleImport = async () => {
    if (selectedProducts.size === 0) {
      alert(CLIENT_CATALOG.ALERT_SELECT_PRODUCTS);
      return;
    }

    try {
      const productIds = Array.from(selectedProducts);

      const clientCode =
        selectedClient?.clientId ||
        clients.find((c) => String(c._id || c.id) === String(selectedClientId))?.clientId;

      if (!clientCode) {
        alert(CLIENT_CATALOG.ALERT_CLIENT_CODE_MISSING);
        return;
      }

      const count = selectedProducts.size;
      await clientCatalogService.addProductsToClientCatalog(clientCode, productIds);

      await reloadCatalogAfterMutation();

      setShowImportModal(false);
      setSelectedProducts(new Set());
      setSearchTerm('');
      setImportFilters(createEmptyImportFilters());

      alert(CLIENT_CATALOG.ALERT_IMPORT_SUCCESS(count));
    } catch (error) {
      console.error('Error importing products:', error);
      alert(CLIENT_CATALOG.ALERT_IMPORT_FAILED);
    }
  };

  const handleFilterChange = (filterName, value) => {
    setImportFilters((prev) => {
      const currentValues = prev[filterName] || [];
      if (currentValues.includes(value)) {
        return {
          ...prev,
          [filterName]: currentValues.filter((v) => v !== value),
        };
      }
      return {
        ...prev,
        [filterName]: [...currentValues, value],
      };
    });
  };

  const clearImportFilters = () => {
    setImportFilters(createEmptyImportFilters());
    setSearchTerm('');
  };

  const clientCodeForApi =
    selectedClient?.clientId ??
    selectedClient?.client_id ??
    clients.find((c) => String(c.id) === String(selectedClientId))?.clientId ??
    clients.find((c) => String(c.id) === String(selectedClientId))?.client_id ??
    '';

  const handleTearSheet = (product, e) => {
    e.stopPropagation();
    setTearSheetProduct(product);
    setTearSheetOpen(true);
  };

  const handleTearSheetSaved = (updated) => {
    if (!updated?.uid) return;
    setClientCatalogProducts((prev) =>
      prev.map((p) => (p.uid === updated.uid ? { ...p, ...updated } : p)),
    );
    setTearSheetProduct((prev) =>
      prev && String(prev.uid) === String(updated.uid) ? { ...prev, ...updated } : prev,
    );
  };

  const handleBOM = (product, e) => {
    e.stopPropagation();
    console.log('BOM for product:', product.itemNo);
    alert(CLIENT_CATALOG.DEV_PHASE);
  };

  const handleProductCycle = (product, e) => {
    e.stopPropagation();
    console.log('Product Cycle for product:', product.itemNo);
    alert(CLIENT_CATALOG.DEV_PHASE);
  };

  const handleRemoveFromCatalog = async (productId) => {
    if (!window.confirm(CLIENT_CATALOG.CONFIRM_REMOVE_FROM_CATALOG)) {
      return;
    }

    try {
      const clientCode =
        selectedClient?.clientId ||
        clients.find((c) => String(c._id || c.id) === String(selectedClientId))?.clientId;

      if (!clientCode) {
        console.error('Client code not found while removing product from catalog');
        return;
      }

      await clientCatalogService.removeProductFromClientCatalog(clientCode, productId);
      await reloadCatalogAfterMutation();
    } catch (error) {
      console.error('Error removing product:', error);
    }
  };

  const activeFilterCount = Object.values(importFilters).reduce(
    (sum, arr) => sum + arr.length,
    0,
  );

  const renderFilterSection = (title, filterKey, options) => (
    <div className="filter-section">
      <h6 className="filter-title">{title}</h6>
      <div className="filter-options">
        {(options || []).length === 0 ? (
          <p className="text-muted small">{CLIENT_CATALOG.FILTER_NO_OPTIONS}</p>
        ) : (
          (options || []).map((option) => (
            <label key={option.id} className="filter-checkbox">
              <input
                type="checkbox"
                checked={importFilters[filterKey]?.includes(option.id) || false}
                onChange={() => handleFilterChange(filterKey, option.id)}
              />
              <span>{option.name}</span>
            </label>
          ))
        )}
      </div>
    </div>
  );

  const closeImportModal = () => {
    setShowImportModal(false);
    setSelectedProducts(new Set());
  };

  return (
    <div className="client-catalog-container">
      <ClientCatalogPageHeader />

      <div className="client-catalog-main">
        <ClientCatalogClientBar
          clients={clients}
          selectedClient={selectedClient}
          selectedClientId={selectedClientId}
          setSelectedClientId={setSelectedClientId}
          showClientDropdown={showClientDropdown}
          setShowClientDropdown={setShowClientDropdown}
          productCount={clientCatalogProducts.length}
          onOpenImport={handleOpenImport}
        />

        <section className="catalog-content-area-full">
          {!selectedClientId ? (
            <div className="empty-state">
              <FaBox size={48} className="text-muted mb-3" />
              <h5>{CLIENT_CATALOG.EMPTY_SELECT_TITLE}</h5>
              <p className="text-muted">{CLIENT_CATALOG.EMPTY_SELECT_BODY}</p>
            </div>
          ) : (
            <>
              {clientCatalogProducts.length === 0 ? (
                <div className="empty-catalog">
                  <FaBox size={48} className="text-muted mb-3" />
                  <h5>{CLIENT_CATALOG.EMPTY_CATALOG_TITLE}</h5>
                  <p className="text-muted">{CLIENT_CATALOG.EMPTY_CATALOG_BODY}</p>
                </div>
              ) : (
                <ClientCatalogProductGrid
                  products={clientCatalogProducts}
                  onTearSheet={handleTearSheet}
                  onBOM={handleBOM}
                  onProductCycle={handleProductCycle}
                  onRemoveFromCatalog={handleRemoveFromCatalog}
                />
              )}
            </>
          )}
        </section>
      </div>

      <TearSheetModal
        open={tearSheetOpen}
        onClose={() => {
          setTearSheetOpen(false);
          setTearSheetProduct(null);
        }}
        clientCode={clientCodeForApi}
        catalogProduct={tearSheetProduct}
        selectedClient={selectedClient}
        onSaved={handleTearSheetSaved}
      />

      {showImportModal && (
        <ClientCatalogImportModal
          onClose={closeImportModal}
          showFiltersInModal={showFiltersInModal}
          setShowFiltersInModal={setShowFiltersInModal}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filterOptions={filterOptions}
          onClearFilters={clearImportFilters}
          activeFilterCount={activeFilterCount}
          selectedProducts={selectedProducts}
          toggleProductSelection={toggleProductSelection}
          importableProducts={importableProducts}
          onImport={handleImport}
          renderFilterSection={renderFilterSection}
        />
      )}
    </div>
  );
};

export default ClientCatalog;
